import { useState, type FormEvent } from 'react'
import type { Facility } from '../../facilities/index.js'
import type { FacilityAdministrator, Invitation, InvitationResult } from '../index.js'
import { SecretBanner } from '../../core/index.js'
import { FacilityAdministratorIcon } from '../../core/index.js'
import { FacilityPicker } from '../../facilities/index.js'
import { Button } from '../../core/index.js'
import { PhoneInput, US_PHONE_PLACEHOLDER } from '../../core/index.js'
import { Save, Send, Trash2, XCircle } from 'lucide-react'
import { InvitationHandOffBanner } from './InviteOperatorModal.js'

interface CreateAdministratorInput {
  email: string
  firstName: string
  lastName: string
  facilityIds: string[]
  /** Optional; shown on the facility's dashboard. */
  phone?: string
}

interface UpdateAdministratorInput {
  facilityIds?: string[]
  rotatePassword?: boolean
  email?: string
  firstName?: string
  lastName?: string
  /** Null clears it. */
  phone?: string | null
}

/** A phone as typed, or undefined when the field is blank. */
function phoneOrUndefined(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function CreateAdministratorInlineForm({
  facility,
  onCreate,
  isPending,
  error,
  onCancel,
}: {
  facility: Facility
  onCreate: (input: CreateAdministratorInput) => void
  isPending: boolean
  error: string | null
  onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    onCreate({ email, firstName, lastName, facilityIds: [facility.facilityId], ...(phoneOrUndefined(phone) ? { phone: phoneOrUndefined(phone) } : {}) })
  }

  const canSubmit = email.trim().length > 0 && firstName.trim().length > 0 && lastName.trim().length > 0

  return (
    <form onSubmit={handleSubmit} className="bg-marine-lt/40 p-3 flex flex-col gap-2 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <input
          type="text"
          placeholder="Last name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <PhoneInput
          aria-label="Phone (optional)"
          placeholder={`Phone (optional): ${US_PHONE_PLACEHOLDER}`}
          value={phone}
          onChange={setPhone}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
      </div>
      <div className="flex items-center gap-3">
        <Button icon={FacilityAdministratorIcon} size="sm" type="submit" disabled={!canSubmit || isPending}>
          {isPending ? 'Inviting…' : 'Invite administrator'}
        </Button>
        <button type="button" onClick={onCancel} className="text-[11px] font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
        {error && <span className="text-[11px] text-coral font-bold">{error}</span>}
      </div>
    </form>
  )
}

function ManageAdministratorPanel({
  administrator,
  facilityId,
  onUpdate,
  onRemove,
  onGenerateResetLink,
  isPending,
  isRemoving,
  isGeneratingResetLink,
}: {
  administrator: FacilityAdministrator
  facilityId: string
  onUpdate: (administratorId: string, input: UpdateAdministratorInput) => Promise<string | undefined>
  onRemove: (administrator: FacilityAdministrator, facilityId: string) => Promise<void>
  onGenerateResetLink: (administratorId: string) => Promise<string>
  isPending: boolean
  isRemoving: boolean
  isGeneratingResetLink: boolean
}) {
  const isLastFacility = administrator.facilityIds.length <= 1
  const [draftFacilityIds, setDraftFacilityIds] = useState(administrator.facilityIds)
  const [firstName, setFirstName] = useState(administrator.firstName)
  const [lastName, setLastName] = useState(administrator.lastName)
  const [email, setEmail] = useState(administrator.email)
  const [phone, setPhone] = useState(administrator.phone ?? '')
  const [rotatedPassword, setRotatedPassword] = useState<string | null>(null)
  const [resetLink, setResetLink] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false)

  function toggleFacility(facilityId: string): void {
    setDraftFacilityIds((prev) => (prev.includes(facilityId) ? prev.filter((id) => id !== facilityId) : [...prev, facilityId]))
  }

  const facilityIdsDirty = JSON.stringify([...draftFacilityIds].sort()) !== JSON.stringify([...administrator.facilityIds].sort())
  const phoneDirty = (phoneOrUndefined(phone) ?? null) !== (administrator.phone ?? null)
  const detailsDirty = firstName !== administrator.firstName || lastName !== administrator.lastName || email !== administrator.email
  const isDirty = facilityIdsDirty || detailsDirty || phoneDirty
  const canSave = isDirty && draftFacilityIds.length > 0 && firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0

  async function handleSave(): Promise<void> {
    setSaveError(null)
    try {
      await onUpdate(administrator.administratorId, {
        ...(facilityIdsDirty ? { facilityIds: draftFacilityIds } : {}),
        ...(detailsDirty ? { firstName, lastName, email } : {}),
        ...(phoneDirty ? { phone: phoneOrUndefined(phone) ?? null } : {}),
      })
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes')
    }
  }

  async function handleRotatePassword(): Promise<void> {
    const password = await onUpdate(administrator.administratorId, { rotatePassword: true })
    if (password) {
      setRotatedPassword(password)
    }
  }

  async function handleRemove(): Promise<void> {
    setSaveError(null)
    try {
      await onRemove(administrator, facilityId)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to remove the administrator')
    }
  }

  async function handleGenerateResetLink(): Promise<void> {
    const resetUrl = await onGenerateResetLink(administrator.administratorId)
    setResetLink(resetUrl)
  }

  return (
    <div className="bg-marine-lt/40 p-3 flex flex-col gap-2 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          type="text"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <input
          type="text"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
        <PhoneInput
          aria-label="Phone (optional)"
          placeholder={`Phone (optional): ${US_PHONE_PLACEHOLDER}`}
          value={phone}
          onChange={setPhone}
          className="border border-marine/20 px-2 py-1.5 text-xs"
        />
      </div>
      <div>
        <div className="text-[11px] text-ink-mid mb-1.5">Facilities this administrator can manage</div>
        <FacilityPicker selectedFacilityIds={draftFacilityIds} onToggle={toggleFacility} />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button icon={Save} size="sm" disabled={!canSave || isPending} onClick={handleSave}>
          Save changes
        </Button>
        <button
          type="button"
          disabled={isPending}
          onClick={handleRotatePassword}
          className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40"
        >
          Rotate password
        </button>
        <button
          type="button"
          disabled={isGeneratingResetLink}
          onClick={handleGenerateResetLink}
          className="text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40"
        >
          {isGeneratingResetLink ? 'Generating…' : 'Send reset link'}
        </button>

        {isConfirmingRemove ? (
          <span className="flex items-center gap-2 ml-auto">
            <span className="text-[11px] text-coral font-bold">
              {isLastFacility ? 'Remove this administrator entirely? Their login stops working.' : 'Remove from this facility?'}
            </span>
            <Button icon={Trash2} size="sm" disabled={isRemoving} onClick={() => void handleRemove()}>
              {isRemoving ? 'Removing…' : 'Remove'}
            </Button>
            <button type="button" onClick={() => setIsConfirmingRemove(false)} className="text-[11px] font-bold text-ink-mid hover:text-ink">
              Cancel
            </button>
          </span>
        ) : (
          <Button icon={Trash2} variant="secondary" size="sm" className="ml-auto" onClick={() => setIsConfirmingRemove(true)}>
            {isLastFacility ? 'Remove administrator' : 'Remove from this facility'}
          </Button>
        )}
      </div>
      {saveError && <span className="text-[11px] text-coral font-bold">{saveError}</span>}
      {rotatedPassword && (
        <SecretBanner
          label="One-time password — copy it now, it won't be shown again"
          value={rotatedPassword}
          onDismiss={() => setRotatedPassword(null)}
        />
      )}
      {resetLink && (
        <SecretBanner
          label="Reset link — send it to this administrator directly. It expires in 1 hour."
          value={resetLink}
          onDismiss={() => setResetLink(null)}
        />
      )}
    </div>
  )
}

/** A pending administrator's panel (invitations plan phase 2): the invitation waits, so there is nothing to rotate - only resend or withdraw. */
function PendingAdministratorPanel({ invitation, onResend, onRevoke, isBusy }: { invitation: Invitation | null; onResend: (invitationId: string) => Promise<InvitationResult>; onRevoke: (invitationId: string) => Promise<unknown>; isBusy: boolean }) {
  const [result, setResult] = useState<InvitationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  if (!invitation) {
    return <div className="mt-2 bg-marine-lt/40 p-3 text-[11px] text-ink-mid">Invited; the invitation record could not be found. Remove the administrator and invite again.</div>
  }
  const when = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  async function run(action: () => Promise<InvitationResult | unknown>): Promise<void> {
    setError(null)
    try {
      const outcome = await action()
      setResult(outcome && typeof outcome === 'object' && 'invitation' in outcome ? (outcome as InvitationResult) : null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work')
    }
  }
  return (
    <div className="bg-marine-lt/40 p-3 flex flex-col gap-2 mt-2 text-[11px]">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-ink-mid">
          Invited {when(invitation.createdAt)} by {invitation.invitedBy.name ?? invitation.invitedBy.sub} · expires {when(invitation.expiresAt)} · they set their own password from the invitation
        </span>
        <Button icon={Send} variant="secondary" size="sm" onClick={() => void run(() => onResend(invitation.invitationId))} disabled={isBusy}>
          Resend
        </Button>
        <Button icon={XCircle} variant="secondary" size="sm" onClick={() => void run(() => onRevoke(invitation.invitationId))} disabled={isBusy}>
          Withdraw
        </Button>
      </div>
      {error && <span className="text-coral font-bold">{error}</span>}
      {result && <InvitationHandOffBanner result={result} onDismiss={() => setResult(null)} />}
    </div>
  )
}

export function FacilityAdministratorControl({
  facility,
  administratorsForFacility,
  onCreate,
  onUpdate,
  onRemove,
  onGenerateResetLink,
  invitations = [],
  onResendInvitation,
  onRevokeInvitation,
  isCreating,
  isUpdating,
  isRemoving,
  isGeneratingResetLink,
  createError,
}: {
  facility: Facility
  administratorsForFacility: FacilityAdministrator[]
  /** Invites the administrator (invitations plan phase 2): the login is made at once, switched off; answers the invitation and, where the deployment cannot email, the link to pass on. */
  onCreate: (input: CreateAdministratorInput) => Promise<InvitationResult | void>
  onUpdate: (administratorId: string, input: UpdateAdministratorInput) => Promise<string | undefined>
  onRemove: (administrator: FacilityAdministrator, facilityId: string) => Promise<void>
  onGenerateResetLink: (administratorId: string) => Promise<string>
  /** The client's facility administrator invitations, for the pending rows. */
  invitations?: readonly Invitation[]
  onResendInvitation?: (invitationId: string) => Promise<InvitationResult>
  onRevokeInvitation?: (invitationId: string) => Promise<unknown>
  isCreating: boolean
  isUpdating: boolean
  isRemoving: boolean
  isGeneratingResetLink: boolean
  createError: string | null
}) {
  const [isCreatingForm, setIsCreatingForm] = useState(false)
  const [openAdministratorId, setOpenAdministratorId] = useState<string | null>(null)
  const [invited, setInvited] = useState<InvitationResult | null>(null)
  const pendingFor = (administratorId: string): Invitation | null => invitations.find((invitation) => invitation.subjectId === administratorId && invitation.status === 'pending') ?? null

  async function handleCreate(input: CreateAdministratorInput): Promise<void> {
    const result = await onCreate(input)
    setInvited(result && 'invitation' in result ? result : null)
    setIsCreatingForm(false)
  }

  if (administratorsForFacility.length === 0) {
    return (
      <div onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-ink-mid">No administrator set</span>
          <button
            type="button"
            onClick={() => setIsCreatingForm((value) => !value)}
            className="text-[11px] font-bold text-marine hover:text-marine-dk"
          >
            {isCreatingForm ? 'Cancel' : 'Set administrator'}
          </button>
        </div>
        {isCreatingForm && (
          <CreateAdministratorInlineForm
            facility={facility}
            onCreate={handleCreate}
            isPending={isCreating}
            error={createError}
            onCancel={() => setIsCreatingForm(false)}
          />
        )}
        {invited && <InvitationHandOffBanner result={invited} onDismiss={() => setInvited(null)} />}
      </div>
    )
  }

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]">
        <span className="text-ink-mid">Administrator:</span>
        {administratorsForFacility.map((administrator, index) => (
          <span key={administrator.administratorId}>
            <button
              type="button"
              onClick={() => setOpenAdministratorId((current) => (current === administrator.administratorId ? null : administrator.administratorId))}
              className="inline-flex items-center gap-1.5 font-bold text-marine hover:text-marine-dk underline decoration-dotted"
            >
              <FacilityAdministratorIcon className="w-3.5 h-3.5" />
              {administrator.firstName} {administrator.lastName}
              {administrator.invitationStatus === 'pending' && <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-ink no-underline">invited · waiting</span>}
            </button>
            {index < administratorsForFacility.length - 1 && <span className="text-ink-mid">,</span>}
          </span>
        ))}
      </div>
      {invited && <InvitationHandOffBanner result={invited} onDismiss={() => setInvited(null)} />}
      {administratorsForFacility.map(
        (administrator) =>
          openAdministratorId === administrator.administratorId && administrator.invitationStatus === 'pending' && onResendInvitation && onRevokeInvitation ? (
            <PendingAdministratorPanel key={administrator.administratorId} invitation={pendingFor(administrator.administratorId)} onResend={onResendInvitation} onRevoke={onRevokeInvitation} isBusy={isUpdating || isRemoving} />
          ) : openAdministratorId === administrator.administratorId && (
            <ManageAdministratorPanel
              key={administrator.administratorId}
              administrator={administrator}
              facilityId={facility.facilityId}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onGenerateResetLink={onGenerateResetLink}
              isPending={isUpdating}
              isRemoving={isRemoving}
              isGeneratingResetLink={isGeneratingResetLink}
            />
          ),
      )}
    </div>
  )
}
